import { Fragment, useState, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Link2 } from 'lucide-react'

function FieldWithPicker({ id, label, value, onChange, onPick, placeholder, fields = [], fieldsLoading }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasFields = fields.length > 0 || fieldsLoading;

  const handlePick = (field) => {
    const val = typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value);
    (onPick || onChange)(val);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { if (hasFields) setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
        />
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-white dark:bg-zinc-700 rounded-lg border border-gray-200 dark:border-zinc-600 shadow-lg">
            {fieldsLoading ? (
              <div className="px-3 py-3 text-center">
                <div className="inline-block h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : fields.length > 0 ? (
              <div className="max-h-40 overflow-y-auto">
                <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400">Submitted data</p>
                {fields.map((field) => {
                  const displayVal = typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value);
                  return (
                    <button
                      key={field.field_id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handlePick(field); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors group"
                    >
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-blue-500">{field.field_name}</p>
                      <p className="text-sm text-gray-900 dark:text-white group-hover:text-blue-700 truncate">{displayVal}</p>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LinkToAgreementModal({
  open,
  setOpen,
  fields = [],
  tenantUser = null,
  onConfirm,
  loading = false,
  fieldsLoading = false,
}) {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [dob, setDob] = useState('');
  const [agreementreference, setAgreementreference] = useState('');

  useEffect(() => {
    if (!open) {
      setFirstname('');
      setLastname('');
      setDob('');
      setAgreementreference('');
    }
  }, [open]);

  // Convert dd/mm/yyyy to yyyy-mm-dd for API
  function dobToApi(displayDob) {
    const parts = displayDob.trim().split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    if (!day || !month || !year || year.length !== 4) return null;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Convert yyyy-mm-dd to dd/mm/yyyy for display
  function dobToDisplay(apiDob) {
    if (!apiDob) return '';
    const parts = apiDob.trim().split('-');
    if (parts.length !== 3) return apiDob;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }

  // Auto-format dob input: insert slashes as user types
  function handleDobChange(val) {
    const digits = val.replace(/[^\d]/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
    } else if (digits.length > 2) {
      formatted = digits.slice(0, 2) + '/' + digits.slice(2);
    }
    setDob(formatted);
  }

  // Wrap setDob for the picker — submitted data may arrive as yyyy-mm-dd
  function handleDobPick(val) {
    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
      setDob(dobToDisplay(val));
    } else {
      setDob(val);
    }
  }

  const handleConfirm = () => {
    if (!firstname.trim() || !lastname.trim() || !dob.trim() || !agreementreference.trim()) return;
    const apiDob = dobToApi(dob);
    if (!apiDob) return;
    onConfirm({ firstname, lastname, dob: apiDob, agreementreference });
  }

  const handleClose = () => {
    if (!loading) setOpen(false);
  }

  const dobIsValid = dob.trim() && dobToApi(dob);
  const isValid = firstname.trim() && lastname.trim() && dobIsValid && agreementreference.trim();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-90"
          leave="ease-in duration-100"
          leaveFrom="opacity-90"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                {/* Header */}
                <div className="mb-5">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-blue-600" />
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Link to Agreement
                    </Dialog.Title>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Enter the details below or use submitted data from the task flow.
                  </p>
                </div>

                {/* Tenant user info */}
                {tenantUser && (
                  <div className="mb-5 rounded-lg bg-gray-50 dark:bg-zinc-700 px-3 py-2.5">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Linking user</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {tenantUser.firstname} {tenantUser.lastname} ({tenantUser.email})
                    </p>
                  </div>
                )}

                {/* Form fields */}
                <div className="space-y-4">
                  <FieldWithPicker
                    id="link_firstname"
                    label="First Name"
                    value={firstname}
                    onChange={setFirstname}
                    placeholder="Enter first name"
                    fields={fields}
                    fieldsLoading={fieldsLoading}
                  />

                  <FieldWithPicker
                    id="link_lastname"
                    label="Last Name"
                    value={lastname}
                    onChange={setLastname}
                    placeholder="Enter last name"
                    fields={fields}
                    fieldsLoading={fieldsLoading}
                  />

                  <FieldWithPicker
                    id="link_dob"
                    label="Date of Birth"
                    value={dob}
                    onChange={handleDobChange}
                    onPick={handleDobPick}
                    placeholder="DD/MM/YYYY"
                    fields={fields}
                    fieldsLoading={fieldsLoading}
                  />

                  <div>
                    <label htmlFor="link_agreementref" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Agreement Reference <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="link_agreementref"
                      type="text"
                      value={agreementreference}
                      onChange={(e) => setAgreementreference(e.target.value)}
                      placeholder="Enter agreement reference"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={loading || !isValid}
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 sm:ml-3 sm:w-auto disabled:opacity-50"
                    onClick={handleConfirm}
                  >
                    {loading ? 'Linking...' : 'Link to Agreement'}
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600 sm:mt-0 sm:w-auto"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
